import { findConsumedFteOrZero } from "src/util/fteUtil";
import { roundFte } from "src/util/roundingStrategy";

/**
 * Determine the allocation status of a person based on their FTE and allocations.
 * This follows the same logic used in insights and the membershipTimeline.
 *
 * @param {Object} person - The person object
 * @param {number} person.fte - The person's total FTE capacity
 * @param {Array} person.allocations - Array of allocation objects with fte field
 * @returns {string} - One of: 'unallocated', 'underAllocated', 'overAllocated', 'fullyAllocated'
 */
export const getAllocationStatus = (person) => {
  if (!person) {
    return null;
  }

  const personFte = person.fte || 0;
  const totalAllocatedFte = findConsumedFteOrZero(person);

  // Unallocated: no allocations or total allocated FTE is 0
  if (roundFte(totalAllocatedFte) === 0) {
    return "unallocated";
  }

  // Round the difference to avoid floating-point comparison issues
  const diff = roundFte(totalAllocatedFte - personFte);

  // Under allocated: allocated FTE is less than person's FTE
  if (diff < 0) {
    return "underAllocated";
  }

  // Over allocated: allocated FTE is greater than person's FTE
  if (diff > 0) {
    return "overAllocated";
  }

  // Fully allocated: allocated FTE equals person's FTE (within rounding tolerance)
  return "fullyAllocated";
};

const allocationStatusList = (status) => {
  if (Array.isArray(status)) {
    return status.filter(Boolean);
  }
  return status ? [status] : [];
};

/**
 * Filter a list of people by allocation status.
 *
 * @param {Array} people - Array of person objects
 * @param {string|string[]} status - Status(es) to match: 'unallocated', 'underAllocated', 'overAllocated'
 * @returns {Array} - Filtered array of people
 */
export const filterPeopleByAllocationStatus = (people, status) => {
  const statuses = allocationStatusList(status);
  if (!statuses.length || !people) {
    return people;
  }

  return people.filter((person) =>
    statuses.includes(getAllocationStatus(person))
  );
};

export const filterPeopleSearchResults = (results, status) => {
  const statuses = allocationStatusList(status);
  if (!statuses.length || !results?.people) {
    return results;
  }

  const options = (results.people.options || []).filter((option) =>
    statuses.includes(getAllocationStatus(option.value?.person))
  );

  return {
    ...results,
    people: {
      ...results.people,
      options,
    },
  };
};

/**
 * Insights pie, badge, and Clear All should ignore the People-only
 * allocationStatus filter, which is not an InsightFilter field.
 */
export const getInsightsFilterCount = (activeFilterCount, filters) =>
  activeFilterCount - (filters?.allocationStatus?.length ? 1 : 0);
